import React, { useEffect } from "react";

import classNames from "classnames";
import { useSelector } from "react-redux";
import { Button, ProgressIndicator, ErrorBanner, ReduxControls } from 'rhombus-react';
import { reduxForm, FieldArray, Field, getFormValues, SubmissionError } from "redux-form";

import { CREATE_BLANK_IMPOSIUM_AUDIO_FORM } from "widgets/forms/list";
import { createBlankImposiumAudioCreative, encodeAudioImposiumGrowls } from "reducers/creatives/VAST";
import { loadCreatives } from 'reducers/Creatives';
import { addNotification } from "reducers/Notifications";
import { inRange, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength, CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { noop } from "utils/common";
import { IMPOSIUM_COMPOSITION_DURATION_OPTIONS } from "utils/constants";

import "widgets/modals/creatives/vast/CreateBlankImposiumAudioModal.css";

const DEFAULT_CREATIVE = {
	name: "",
	compositionName: "",
	duration: { label: "15 seconds", value: 15 },
	customDuration: 30,
};

const MAX_NEW_CREATIVES = 10;
const AUDIO_DURATION_CONSTRAINTS = { min: 1, max: 120 };

const customDurationRange = inRange(AUDIO_DURATION_CONSTRAINTS);

export function formatFormValues(values = {}) {
	const { projectName, creatives = []} = values;

	const formattedCreatives = creatives.map((creative) => {
		const { name, compositionName, duration, customDuration } = creative;
		const formattedDuration = duration?.value === "custom" ? customDuration : duration?.value;

		return { name, compositionName, duration: formattedDuration };
	});

	return { projectName, creatives: formattedCreatives };
}

function AudioCreativeFields({ fields, formValues }) {
	const addCreative = () => fields.push({ ...DEFAULT_CREATIVE });
	const removeCreative = (index) => {
		if (fields.length > 1) {
			fields.remove(index);
		}
	};

	return (
		<div className="creatives-list">
			{fields.map((member, index) => {
				const creative = formValues?.creatives?.[index] || {};
				const isCustomDuration = creative.duration?.value === "custom";
				const isDurationValid = isCustomDuration ? !!creative.customDuration : !!creative.duration;

				return (
					<div key={member} className="creative-item">
						<div className="creative-row">
							<Field
								name={`${member}.name`}
								className="creative-name-field"
								label="Creative Name"
								required
								showCharCount
								charMax={CREATIVE_NAME_MAX_LENGTH}
								validate={[requiredNotNull, creativeNameAllowedChars, creativeNameMaxLength]}
								component={ReduxControls.Input}
								placeholder="Enter Creative Name..."
							/>
							<Field
								name={`${member}.compositionName`}
								className="composition-name-field"
								label="Default Composition Name"
								required
								showCharCount
								charMax={CREATIVE_NAME_MAX_LENGTH}
								validate={[requiredNotNull, creativeNameAllowedChars, creativeNameMaxLength]}
								component={ReduxControls.Input}
								placeholder="Enter Composition Name..."
							/>
							{fields.length > 1 && (
								<button type="button" className="remove-creative-btn" onClick={() => removeCreative(index)} title="Remove creative">
									<i className="fal fa-times-circle" />
								</button>
							)}
						</div>
						<div className="duration-row">
							<Field
								name={`${member}.duration`}
								label="Duration"
								className={classNames({ "duration-field": true, pristine: !isDurationValid })}
								isClearable={false}
								required
								validate={requiredNotNull}
								component={ReduxControls.Select}
								options={IMPOSIUM_COMPOSITION_DURATION_OPTIONS}
								onMenuScrollToBottom={noop}
							/>
							{isCustomDuration && (
								<>
									<Field
										name={`${member}.customDuration`}
										className="custom-duration"
										type="number"
										normalize={value => Number(value)}
										required
										validate={[requiredNotNull, customDurationRange]}
										component={ReduxControls.Input}
									/>
									<span className="duration-unit">seconds</span>
								</>
							)}
						</div>
					</div>
				);
			})}
			{fields.length < MAX_NEW_CREATIVES && (
				<Button className="add-creative-btn" onClick={addCreative} style="link">
					+ Add another creative
				</Button>
			)}
		</div>
	);
}

function AudioCreativesFieldArray(props) {
	const { fields } = props;

	useEffect(() => {
		if (fields?.length === 0) {
			fields.push({ ...DEFAULT_CREATIVE });
		}
	}, [fields]);

	return <AudioCreativeFields {...props} />;
}

export function NewImposiumAudioWithCreativesForm({ handleSubmit, submitting, error }) {
	const formValues = useSelector(getFormValues(CREATE_BLANK_IMPOSIUM_AUDIO_FORM)) || {};

	return (
		<div className="create-blank-imposium-audio">
			<form id={CREATE_BLANK_IMPOSIUM_AUDIO_FORM} onSubmit={handleSubmit}>
				<ProgressIndicator key="loading" isLoading={submitting} />
				{error && (
					<ErrorBanner
						title="Sorry, please fix the errors below and try again."
						messages={[error]}
					/>
				)}
				<Field
					name="projectName"
					className="project-name-field"
					label="Imposium Project Name"
					required
					showCharCount
					charMax={CREATIVE_NAME_MAX_LENGTH}
					validate={[requiredNotNull, creativeNameAllowedChars, creativeNameMaxLength]}
					component={ReduxControls.Input}
				/>
				<div>
					<FieldArray component={AudioCreativesFieldArray} name="creatives" formValues={formValues} />
				</div>
			</form>
		</div>
	);
}

NewImposiumAudioWithCreativesForm.displayName = "NewImposiumAudioWithCreativesForm";

const DEFAULT_INITIAL_VALUES = {
	projectName: "",
	creatives: [{ ...DEFAULT_CREATIVE }],
};

export default reduxForm({
	form: CREATE_BLANK_IMPOSIUM_AUDIO_FORM,
	initialValues: DEFAULT_INITIAL_VALUES,
	onSubmit(values, dispatch, props) {
		const formattedValues = formatFormValues(values);
		return dispatch(createBlankImposiumAudioCreative(
			props.libraryId,
			formattedValues.projectName,
			formattedValues.creatives
		)).then((resp) => {
			if (!resp.success) {
				throw new SubmissionError({
					_error: resp.payload?.errorObj?.description || "Failed to create audio project",
				});
			}
			return resp;
		});
	},
	onSubmitSuccess(result, dispatch, props) {
		const numCreatives = result?.payload?.data?.length || 1;
		dispatch(addNotification({
			message: encodeAudioImposiumGrowls(numCreatives),
			type: "success",
		}));
		dispatch(loadCreatives(props.libraryId));
		props.onClose();
	},
})(NewImposiumAudioWithCreativesForm);
